<script setup lang="ts">
import { ArrowRight } from '@element-plus/icons-vue'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed, h, ref } from 'vue'
import WujieVue from 'wujie-vue3'

import dayjs from 'dayjs'
import { messageRecordLatestUnread, messageRecordReadStatusStats, recordPageList, recordSingleMarkRead } from '@mp/api/sdpMessage/MessageV2Controller.ts'
import { ElButton, ElIcon } from 'element-plus'

const { bus } = WujieVue

const tabActive = ref<1 | 2 | 3>(1)

// #查询消息数量
const { data: messageStats, refetch: refetchReadStatusStats } = useQuery({
  queryKey: ['message/record/read-status-stats'],
  queryFn: () => messageRecordReadStatusStats({
    targetAppId: 'WEB_B',
  }),
  select: res => res.series,
})

// ?获取待办的消息数量
const todoTaskStats = computed(() => {
  return messageStats.value?.find(item => item.messageType === 'todo_task')
})

// ?获取系统通知
const systemStats = computed(() => {
  return messageStats.value?.find(item => item.messageType === 'system_notification')
})

// #设置为已读
const { mutate: markRead } = useMutation({
  mutationFn: recordSingleMarkRead,
})

// #获取最近一条未读的消息
useQuery({
  queryKey: ['message/record/latest-unread'],
  queryFn: () => messageRecordLatestUnread({
    targetAppId: 'WEB_B',
  }),
  select(res) {
    const messageId = res.data?.messageId
    const messageDsl = res.data?.messageDsl
    const buttons = messageDsl?.display?.buttons

    const displayType = messageDsl?.display?.displayType

    const cancelButtonText = buttons?.find(i => i.action?.type === 'close')?.text || '关闭'
    const confirmButton = buttons?.find(i => i.style?.type === 'primary')

    if (displayType === 'toast') {
      ElNotification({
        title: messageDsl?.content?.title || '系统提示',
        duration: 0,
        message: h('div', [
          messageDsl?.content.body,
          h(
            ElButton,
            {
              type: 'warning',
              link: true,
              class: 'ml-2 -translate-y-[2px]',
              onClick: () => {
                ElNotification.closeAll()
                bus.$emit('notice:view', messageId)
              },
            },
            [
              '去查看',
              h(
                ElIcon,
                h(ArrowRight),
              ),
            ],
          ),
        ]),
        type: 'warning',
      })

      return
    }

    if (displayType === 'modal') {
      ElMessageBox({
        customStyle: {
          '--el-messagebox-padding-primary': '20px',
          '--el-messagebox-width': '480px',
        },
        title: messageDsl?.content?.title || '系统提示',
        message: messageDsl?.content.body,
        type: 'warning',
        showCancelButton: Boolean(cancelButtonText),
        cancelButtonText,
        confirmButtonText: confirmButton?.text || '确认',
      }).then(() => {
        if (!messageId)
          return
        markRead({ messageId })
        if (confirmButton?.action?.type === 'open_url')
          window.open(confirmButton?.action?.target)
      }).catch(() => {

      }).finally(() => {

      })
    }

    return res.data
  },
})

// #获取待办事项
const { data: todoList, refetch: refetchTodo } = useQuery({
  queryKey: ['message/record/page/list:todo'],
  queryFn: () => recordPageList({
    pageNum: 1,
    pageSize: 3,
    targetAppId: 'WEB_B',
    messageType: 'todo_task',
  }),
  select: res => res?.series ?? [],
})

// #获取系统通知
const { data: systemList, refetch: refetchSystem } = useQuery({
  queryKey: ['message/record/page/list:system'],
  queryFn: () => recordPageList({
    pageNum: 1,
    pageSize: 3,
    targetAppId: 'WEB_B',
    messageType: 'system_notification',
  }),
  select: res => res?.series ?? [],
})

bus.$on('notice:detail:close', () => {
  refetchTodo()
  refetchSystem()
  refetchReadStatusStats()
})
</script>

<template>
  <section class="bg-white px-4 pt-2 pb-2 rounded-lg relative">
    <ElButton link type="primary" class="absolute right-[8px] top-[18px] z-1" @click="bus.$emit('notice:open', { tab: tabActive })">
      查看全部
      <ElIcon class="ml-1">
        <ArrowRight />
      </ElIcon>
    </ElButton>

    <el-tabs v-model="tabActive" style="--el-border-color-light: transparent">
      <el-tab-pane :name="1">
        <template #label>
          <el-badge :is-dot="Boolean(todoTaskStats?.unreadCount)" :offset="[7, 0]" :show-zero="false">
            待办事项
          </el-badge>
        </template>

        <el-empty v-if="!todoList?.length" description="暂无数据" :image-size="100" />
        <div v-for="item in todoList" :key="item.messageId" class="p-4 bg-n-10 text-sm rounded-lg mb-2 cursor-pointer" @click="bus.$emit('notice:view', item.messageId)">
          <div class=" flex">
            <h3 class=" text-n-800 font-medium flex-1 mr-4 hover:text-primary cursor-pointer">
              <i
                v-if="!item.read"
                class="w-2 h-2 border-1 border-white border-solid rounded-full bg-error inline-block -translate-y-[2px]
            "
              />
              {{ item?.content?.title }}
            </h3>
            <span class=" text-n-90">{{ dayjs(item.sendTime).fromNow() }}</span>
          </div>
          <p class=" text-n-90 mt-1 line-clamp-2">
            {{ item.content?.body }}
          </p>
        </div>
      </el-tab-pane>
      <el-tab-pane :name="2">
        <template #label>
          <el-badge :is-dot="Boolean(systemStats?.unreadCount)" :offset="[7, 0]" :show-zero="false">
            系统通知
          </el-badge>
        </template>

        <el-empty v-if="!systemList?.length" description="暂无数据" :image-size="100" />
        <div v-for="item in systemList" :key="item.messageId" class="py-2 flex text-sm cursor-pointer" @click="bus.$emit('notice:view', item.messageId)">
          <label class="text-n-800 flex-1 mr-4 hover:text-primary cursor-pointer">
            <i
              v-if="!item.read"
              class="w-2 h-2 mr-1 border-1 border-white border-solid rounded-full bg-error inline-block -translate-y-[2px]
            "
            />
            {{ item?.content?.title }}
            <ElIcon class="translate-y-[2px]">
              <ArrowRight />
            </ElIcon>
          </label>
          <span v-if="item.sendTime" class=" text-n-90 ml-auto">{{ dayjs(item.sendTime).fromNow() }}</span>
        </div>
      </el-tab-pane>
      <!-- <el-tab-pane label="平台公告" :name="3">
        <div v-for="item in 10" :key="item" class="py-2 flex text-sm" @click="bus.$emit('notice:view')">
          <label class="text-n-800 flex-1 mr-4 hover:text-primary cursor-pointer">
            <i
              class="w-2 h-2 mr-1 border-1 border-white border-solid rounded-full bg-error inline-block -translate-y-[2px]
            "
            />
            需要通知的退票标题需要通知的退票标题需要通知的退票标题
            <el-icon class="translate-y-[2px]">
              <ArrowRight />
            </el-icon>
          </label>
          <span class=" text-n-90 ml-auto">2小时前</span>
        </div>
      </el-tab-pane> -->
    </el-tabs>

    <!-- <div class="space-y-3 text-sm">
      <p v-for="item in 10" :key="item" class="flex leading-none items-center">
        <a href="" class="flex-1 truncate hover:text-primary">需要通知的退票标题需要通知的退票标题需要通知的退票标题需要通知的退票标题</a>
        <el-icon>
          <ArrowRight />
        </el-icon>
        <span class="text-n-90 ml-4">刚刚</span>
      </p>
    </div> -->
  </section>
</template>

<style scoped>
:deep(.el-tabs__item) {
  padding: 0 10px;
}
</style>
